import { useState, useEffect, useCallback, useMemo } from "react";
import FormOperazione from "./FormOperazione";

import { API } from "../config";

// ── Stili coerenti con DiarioBookmaker.js / DiarioCapitale.js ──
const FONT = "'DM Sans', sans-serif";
const MONO = "'IBM Plex Mono', monospace";

const stili = {
  card: {
    background: "#fff", borderRadius: 10,
    border: "1px solid #E2E8F0",
    padding: "16px 20px", marginBottom: 16,
  },
  intestazioneCard: {
    display: "flex", justifyContent: "space-between",
    alignItems: "center", marginBottom: 14, gap: 16,
    flexWrap: "wrap",
  },
  intestazioneSinistra: {
    display: "flex", flexDirection: "column", gap: 4,
  },
  titoloCard: {
    fontSize: 15, fontWeight: 700, color: "#1E293B", margin: 0,
  },
  rigaTotale: {
    display: "flex", alignItems: "baseline", gap: 14,
    fontSize: 13, color: "#64748B", marginTop: 2,
  },
  totaleLabel: {
    fontSize: 11, fontWeight: 700, color: "#64748B",
    textTransform: "uppercase", letterSpacing: 0.3,
  },
  totaleValore: {
    fontFamily: MONO, fontSize: 16, fontWeight: 700,
  },
  btn: {
    fontFamily: FONT, fontSize: 13, fontWeight: 600,
    padding: "7px 16px", borderRadius: 6, border: "none",
    cursor: "pointer", transition: "background 0.15s",
  },
  btnPrimary: {
    background: "#2563EB", color: "#fff",
  },
  btnIcona: {
    fontFamily: FONT, fontSize: 13,
    padding: "4px 8px", borderRadius: 4, border: "none",
    cursor: "pointer", background: "transparent",
  },
  // ── Filtri rapidi ──
  filtriRapidiRiga: {
    display: "flex", gap: 6, alignItems: "center",
    marginBottom: 12, flexWrap: "wrap",
  },
  chipFiltro: {
    fontFamily: FONT, fontSize: 12, fontWeight: 600,
    padding: "6px 12px", borderRadius: 16,
    border: "1px solid #CBD5E1", background: "#fff",
    color: "#475569", cursor: "pointer",
    transition: "background 0.15s, color 0.15s, border-color 0.15s",
  },
  chipFiltroAttivo: {
    background: "#2563EB", color: "#fff",
    borderColor: "#2563EB",
  },
  chipFiltroDisabilitato: {
    background: "#F1F5F9", color: "#94A3B8",
    borderColor: "#E2E8F0", cursor: "not-allowed",
  },
  // ── Filtri data personalizzati ──
  filtriPersRiga: {
    display: "flex", gap: 12, alignItems: "flex-end",
    marginBottom: 12, flexWrap: "wrap",
    paddingTop: 8, borderTop: "1px dashed #E2E8F0",
  },
  filtroCol: {
    display: "flex", flexDirection: "column", gap: 4,
  },
  filtroLabel: {
    fontSize: 11, fontWeight: 600, color: "#64748B",
    textTransform: "uppercase", letterSpacing: 0.3,
  },
  filtroInput: {
    fontFamily: FONT, fontSize: 13, padding: "6px 10px",
    borderRadius: 6, border: "1px solid #CBD5E1",
    background: "#fff", color: "#1E293B", outline: "none",
    minWidth: 140,
  },
  // ── Tabella ──
  tabellaWrap: {
    overflowX: "auto", border: "1px solid #E2E8F0",
    borderRadius: 8, background: "#fff",
  },
  tabella: {
    width: "100%", borderCollapse: "collapse", fontFamily: FONT,
    minWidth: 1300,
  },
  th: {
    fontSize: 11, fontWeight: 700, color: "#64748B",
    padding: "10px 12px", textAlign: "left",
    borderBottom: "2px solid #E2E8F0", whiteSpace: "nowrap",
    background: "#F8FAFC",
    textTransform: "uppercase", letterSpacing: 0.3,
  },
  thNum: {
    fontSize: 11, fontWeight: 700, color: "#64748B",
    padding: "10px 12px", textAlign: "right",
    borderBottom: "2px solid #E2E8F0", whiteSpace: "nowrap",
    background: "#F8FAFC",
    textTransform: "uppercase", letterSpacing: 0.3,
  },
  thCenter: {
    fontSize: 11, fontWeight: 700, color: "#64748B",
    padding: "10px 12px", textAlign: "center",
    borderBottom: "2px solid #E2E8F0", whiteSpace: "nowrap",
    background: "#F8FAFC",
    textTransform: "uppercase", letterSpacing: 0.3,
  },
  td: {
    fontSize: 13, padding: "10px 12px", color: "#334155",
    borderBottom: "1px solid #F1F5F9", whiteSpace: "nowrap",
    fontFamily: FONT,
  },
  tdNum: {
    fontSize: 13, padding: "10px 12px", color: "#334155",
    borderBottom: "1px solid #F1F5F9", whiteSpace: "nowrap",
    fontFamily: MONO, textAlign: "right",
  },
  tdCenter: {
    fontSize: 13, padding: "10px 12px", color: "#334155",
    borderBottom: "1px solid #F1F5F9", whiteSpace: "nowrap",
    textAlign: "center",
  },
  tdNa: {
    color: "#94A3B8", fontStyle: "italic",
  },
  badgeTipo: {
    display: "inline-block", padding: "2px 10px",
    borderRadius: 12, fontSize: 11, fontWeight: 600,
  },
  // ── Esito inline (dropdown senza bordi visibili) ──
  esitoSelect: {
    fontFamily: FONT, fontSize: 11, fontWeight: 600,
    padding: "2px 6px", borderRadius: 12,
    border: "1px solid transparent",
    cursor: "pointer", outline: "none",
    appearance: "none", WebkitAppearance: "none", MozAppearance: "none",
    paddingRight: 18,
    backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='8' height='5' viewBox='0 0 8 5'><path fill='%2364748B' d='M0 0l4 5 4-5z'/></svg>\")",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "right 6px center",
  },
  vuoto: {
    textAlign: "center", color: "#94A3B8",
    padding: "40px 16px", fontSize: 13,
  },
  loading: {
    textAlign: "center", color: "#94A3B8",
    padding: "30px 16px", fontSize: 13,
  },
  // ── Dialog conferma cancellazione ──
  dialogOverlay: {
    position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
    background: "rgba(15, 23, 42, 0.5)",
    display: "flex", alignItems: "center", justifyContent: "center",
    zIndex: 1100,
  },
  dialog: {
    background: "#fff", borderRadius: 10,
    padding: "20px 24px", width: 380, maxWidth: "90vw",
    fontFamily: FONT,
    boxShadow: "0 10px 40px rgba(0,0,0,0.2)",
  },
  dialogTitolo: {
    fontSize: 15, fontWeight: 700, color: "#1E293B",
    marginBottom: 8,
  },
  dialogTesto: {
    fontSize: 13, color: "#475569", marginBottom: 18,
    lineHeight: 1.5,
  },
  dialogAzioni: {
    display: "flex", justifyContent: "flex-end", gap: 10,
  },
  btnSecondary: {
    background: "#F1F5F9", color: "#475569",
    border: "1px solid #CBD5E1",
  },
  btnDanger: {
    background: "#DC2626", color: "#fff",
  },
};

const COLORI_TIPO = {
  "Punta": { bg: "#DBEAFE", color: "#1E40AF" },
  "Banca": { bg: "#FCE7F3", color: "#9D174D" },
};

const COLORI_ESITO = {
  "Open":  { bg: "#F1F5F9", color: "#64748B" },
  "Vinta": { bg: "#DCFCE7", color: "#166534" },
  "Persa": { bg: "#FEE2E2", color: "#991B1B" },
};

// ── Formattazione ───────────────────────────────────────
const NA = <span style={stili.tdNa}>n.a.</span>;

// v125: helper che separa migliaia con "." e decimali con "," senza usare
// toLocaleString (bug noto V8 sui numeri 1000-9999, già risolto in v124).
function _formatNumeroIT(n, dec = 2) {
  const negativo = n < 0;
  const abs = Math.abs(n);
  const intero = Math.trunc(abs);
  const decAttesi = Math.pow(10, dec);
  const decRound = Math.round((abs - intero) * decAttesi);
  const decStr = String(decRound).padStart(dec, "0");
  const interoStr = String(intero).replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return (negativo ? "-" : "") + interoStr + (dec > 0 ? "," + decStr : "");
}

function formatEur(v) {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  if (!isFinite(n)) return null;
  return _formatNumeroIT(n, 2) + " €";
}

function formatEurConSegno(v) {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  if (!isFinite(n)) return null;
  const segno = n > 0 ? "+" : "";
  return segno + _formatNumeroIT(n, 2) + " €";
}

function formatNumero(v, dec = 2) {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  if (!isFinite(n)) return null;
  return _formatNumeroIT(n, dec);
}

function formatPct(v) {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  if (!isFinite(n)) return null;
  return _formatNumeroIT(n, 2) + "%";
}

function formatData(s) {
  if (!s) return null;
  try {
    return new Date(s).toLocaleDateString("it-IT", {
      day: "2-digit", month: "2-digit", year: "2-digit",
    });
  } catch { return s; }
}

function formatOra(s) {
  if (!s) return null;
  return s.slice(0, 5);
}

// Helper per mostrare valore o "n.a."
function celOValNa(formatted) {
  return formatted === null || formatted === undefined ? NA : formatted;
}

// ── Calcola le date dei filtri rapidi ──
function dataIso(d) {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

// ── Helper stagioni dinamiche da diario_stagioni ─────────
// Ritorna la stagione "in corso": data odierna nel range [data_inizio, data_fine]
// oppure stagione con data_fine NULL (in corso a oltranza).
// stagioniDB arriva ordinato data_inizio DESC dal backend.
function trovaStagioneInCorso(stagioniDB) {
  if (!Array.isArray(stagioniDB) || stagioniDB.length === 0) return null;
  const oggiIsoStr = dataIso(new Date());
  for (const s of stagioniDB) {
    if (!s.data_inizio) continue;
    if (oggiIsoStr < s.data_inizio) continue; // stagione futura
    if (s.data_fine === null || s.data_fine === undefined || s.data_fine === "") {
      return s; // in corso a oltranza
    }
    if (oggiIsoStr <= s.data_fine) {
      return s; // dentro al range
    }
  }
  return null;
}

// Ritorna la stagione "precedente": quella subito dopo (in ordinamento desc)
// la stagione in corso. Se non c'è in-corso, ritorna la prima della lista (la più recente conclusa).
function trovaStagionePrecedente(stagioniDB) {
  if (!Array.isArray(stagioniDB) || stagioniDB.length === 0) return null;
  const inCorso = trovaStagioneInCorso(stagioniDB);
  if (!inCorso) {
    // Nessuna in corso → la "precedente" è la più recente conclusa = prima della lista DESC
    return stagioniDB[0] || null;
  }
  const idx = stagioniDB.findIndex(s => s.id === inCorso.id);
  if (idx < 0 || idx + 1 >= stagioniDB.length) return null;
  return stagioniDB[idx + 1];
}

function calcolaIntervallo(filtro, stagioniDB = []) {
  const oggi = new Date();
  const oggiIso = dataIso(oggi);
  let da = "";
  switch (filtro) {
    case "tutto":
      return { da: "", a: "" };
    case "stagione": {
      // Stagione "in corso" letta dalla tabella diario_stagioni.
      // Fallback (Opzione 2): se nessuna stagione configurata → ritorno {da:"", a:""}
      // (equivalente a "Tutto"). Il chip viene comunque disabilitato lato UI.
      const inCorso = trovaStagioneInCorso(stagioniDB);
      if (!inCorso) return { da: "", a: "" };
      const daD = inCorso.data_inizio;
      const aD = (inCorso.data_fine && inCorso.data_fine !== "")
        ? inCorso.data_fine
        : oggiIso; // se stagione "a oltranza", chiudo a oggi
      return { da: daD, a: aD };
    }
    case "stagionePrecedente": {
      const prec = trovaStagionePrecedente(stagioniDB);
      if (!prec) return { da: "", a: "" };
      const daD = prec.data_inizio;
      const aD = (prec.data_fine && prec.data_fine !== "")
        ? prec.data_fine
        : oggiIso;
      return { da: daD, a: aD };
    }
    case "annoSolare":
      da = `${oggi.getFullYear()}-01-01`;
      return { da, a: oggiIso };
    case "ultimi12mesi": {
      const d = new Date(oggi);
      d.setFullYear(d.getFullYear() - 1);
      d.setDate(d.getDate() + 1);
      return { da: dataIso(d), a: oggiIso };
    }
    case "ultimoSemestre": {
      const d = new Date(oggi);
      d.setMonth(d.getMonth() - 6);
      d.setDate(d.getDate() + 1);
      return { da: dataIso(d), a: oggiIso };
    }
    case "ultimoTrimestre": {
      const d = new Date(oggi);
      d.setMonth(d.getMonth() - 3);
      d.setDate(d.getDate() + 1);
      return { da: dataIso(d), a: oggiIso };
    }
    case "ultimoMese": {
      const d = new Date(oggi);
      d.setMonth(d.getMonth() - 1);
      d.setDate(d.getDate() + 1);
      return { da: dataIso(d), a: oggiIso };
    }
    default:
      return { da: "", a: "" };
  }
}

// ── Componente principale ──────────────────────────────
export default function DiarioOperazioni({ onProfittoCambiato }) {
  const [operazioni, setOperazioni] = useState([]);
  const [bookmakers, setBookmakers] = useState({});
  const [sistemiMappa, setSistemiMappa] = useState({});
  const [loading, setLoading] = useState(true);
  const [errore, setErrore] = useState(null);

  // Filtri (default: stagione in corso da diario_stagioni, fallback "tutto" se assente)
  const [filtroRapido, setFiltroRapido] = useState("stagione");
  // Inizializzazione "morbida": parto con range vuoto. Quando arriva la lista stagioni
  // dal backend (useEffect dedicato), il primo render utile ricalcola il range corretto.
  const [daData, setDaData] = useState("");
  const [aData, setAData]   = useState("");

  // Lista stagioni da diario_stagioni (alimenta filtri "Stagione in corso" e "Stagione precedente")
  const [stagioniDB, setStagioniDB] = useState([]);
  const [stagioniCaricate, setStagioniCaricate] = useState(false);

  // Modal
  const [modalAperto, setModalAperto] = useState(false);
  const [operazioneInModifica, setOperazioneInModifica] = useState(null);

  // Conferma cancellazione
  const [confermaCanc, setConfermaCanc] = useState(null);

  // Carica bookmakers
  useEffect(() => {
    fetch(`${API}/diario/bookmaker`)
      .then(r => r.ok ? r.json() : [])
      .then(data => {
        const mappa = {};
        data.forEach(b => { mappa[b.id] = b.nome; });
        setBookmakers(mappa);
      })
      .catch(() => {});
  }, []);

  // Carica sistemi (per mostrare il nome al posto dell'id)
  useEffect(() => {
    fetch(`${API}/sistemi`)
      .then(r => r.ok ? r.json() : [])
      .then(data => {
        if (Array.isArray(data)) {
          const mappa = {};
          data.forEach(s => { mappa[s.id] = s.nome || s.id; });
          setSistemiMappa(mappa);
        }
      })
      .catch(() => {});
  }, []);

  // Carica stagioni dalla tabella diario_stagioni.
  // Al completamento: se il filtro corrente è "stagione" (default) ma non c'è
  // stagione in corso → applica Opzione 2 (switch automatico a "tutto") così
  // l'utente vede subito qualcosa nella tabella invece di aprirsi su vuoto.
  useEffect(() => {
    fetch(`${API}/diario/stagioni`)
      .then(r => r.ok ? r.json() : [])
      .then(data => {
        const lista = Array.isArray(data) ? data : [];
        setStagioniDB(lista);
        setStagioniCaricate(true);
        // Applico l'intervallo per il filtro corrente con la lista appena ricevuta.
        const inCorso = trovaStagioneInCorso(lista);
        if (filtroRapido === "stagione" && !inCorso) {
          // Opzione 2: nessuna stagione in corso → fallback a "tutto"
          setFiltroRapido("tutto");
          setDaData("");
          setAData("");
        } else if (filtroRapido === "stagione" || filtroRapido === "stagionePrecedente") {
          const { da, a } = calcolaIntervallo(filtroRapido, lista);
          setDaData(da);
          setAData(a);
        }
      })
      .catch(() => { setStagioniCaricate(true); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Quando cambia il filtro rapido, aggiorna le date
  const cambiaFiltroRapido = (id) => {
    setFiltroRapido(id);
    if (id !== "personalizzato") {
      const { da, a } = calcolaIntervallo(id, stagioniDB);
      setDaData(da);
      setAData(a);
    }
  };

  // Carica operazioni
  const ricarica = useCallback(async () => {
    setLoading(true);
    setErrore(null);
    try {
      const params = new URLSearchParams();
      if (daData) params.set("da_data", daData);
      if (aData)  params.set("a_data", aData);
      const url = `${API}/diario/operazioni${params.toString() ? "?" + params.toString() : ""}`;
      const r = await fetch(url);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setOperazioni(Array.isArray(data) ? data : []);
    } catch (e) {
      setErrore(e.message || "Errore di rete");
    } finally {
      setLoading(false);
    }
  }, [daData, aData]);

  useEffect(() => { ricarica(); }, [ricarica]);

  // Totale P/L netto sulle operazioni filtrate
  const totalePL = useMemo(() => {
    let tot = 0;
    operazioni.forEach(op => {
      const v = op.profitto_netto_eur;
      if (v !== null && v !== undefined && v !== "") tot += Number(v);
    });
    return tot;
  }, [operazioni]);

  const apriNuova = () => {
    setOperazioneInModifica(null);
    setModalAperto(true);
  };
  const apriModifica = (op) => {
    setOperazioneInModifica(op);
    setModalAperto(true);
  };
  const chiudiModal = () => {
    setModalAperto(false);
    setOperazioneInModifica(null);
  };
  const dopoSalva = () => {
    chiudiModal();
    ricarica();
    if (onProfittoCambiato) onProfittoCambiato();
  };

  // Cambio esito inline (riusa endpoint PUT con tutto il record)
  const cambiaEsitoInline = async (op, nuovoEsito) => {
    try {
      // Riapro l'operazione per mandare un PUT completo (il backend richiede tutti i campi obbligatori)
      const payload = {
        data_partita: op.data_partita,
        casa: op.casa,
        trasferta: op.trasferta,
        mercato: op.mercato,
        tipo_punta_banca: op.tipo_punta_banca,
        quota_effettiva: op.quota_effettiva,
        bookmaker_id: op.bookmaker_id,
        esito_operazione: nuovoEsito,
        ora_partita: op.ora_partita || undefined,
        competizione: op.competizione || undefined,
        sistema_id: op.sistema_id || undefined,
        quota_teorica: op.quota_teorica ?? undefined,
        fee_pct: op.fee_pct ?? undefined,
        size_pct: op.size_pct ?? undefined,
        note: op.note || undefined,
      };
      // Rimuovo gli undefined
      Object.keys(payload).forEach(k => payload[k] === undefined && delete payload[k]);

      const r = await fetch(`${API}/diario/operazioni/${op.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      if (data.errore) throw new Error(data.errore);
      ricarica();
      if (onProfittoCambiato) onProfittoCambiato();
    } catch (e) {
      setErrore(e.message || "Errore aggiornamento esito");
    }
  };

  const confermaElimina = async () => {
    if (!confermaCanc) return;
    try {
      const r = await fetch(`${API}/diario/operazioni/${confermaCanc.id}`, {
        method: "DELETE",
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      if (data.errore) throw new Error(data.errore);
      setConfermaCanc(null);
      ricarica();
      if (onProfittoCambiato) onProfittoCambiato();
    } catch (e) {
      setErrore(e.message || "Errore eliminazione");
      setConfermaCanc(null);
    }
  };

  // ── Render ──
  const filtriRapidi = [
    { id: "stagione",            label: "Stagione in corso" },
    { id: "stagionePrecedente",  label: "Stagione precedente" },
    { id: "annoSolare",          label: "Anno solare" },
    { id: "ultimi12mesi",        label: "Ultimi 12 mesi" },
    { id: "ultimoSemestre",      label: "Ultimo semestre" },
    { id: "ultimoTrimestre",     label: "Ultimo trimestre" },
    { id: "ultimoMese",          label: "Ultimo mese" },
    { id: "tutto",               label: "Tutto" },
    { id: "personalizzato",      label: "Personalizzato" },
  ];

  // Disponibilità chip dinamici (solo "stagione" e "stagionePrecedente").
  // Calcolati ad ogni render, ma il costo è trascurabile su array piccoli.
  const stagioneInCorsoEsiste = trovaStagioneInCorso(stagioniDB) !== null;
  const stagionePrecedenteEsiste = trovaStagionePrecedente(stagioniDB) !== null;
  const isChipDisabilitato = (id) => {
    if (!stagioniCaricate) return false; // durante il caricamento, non disabilito (evita flicker)
    if (id === "stagione") return !stagioneInCorsoEsiste;
    if (id === "stagionePrecedente") return !stagionePrecedenteEsiste;
    return false;
  };
  const tooltipChip = (id) => {
    if (id === "stagione" && !stagioneInCorsoEsiste) {
      return "Nessuna stagione in corso configurata. Vai alla tab ⚙️ Impostazioni → Stagioni per aggiungerne una.";
    }
    if (id === "stagionePrecedente" && !stagionePrecedenteEsiste) {
      return "Nessuna stagione precedente disponibile. Configura più stagioni nella tab ⚙️ Impostazioni → Stagioni.";
    }
    return undefined;
  };

  const totaleColor = totalePL > 0 ? "#2E7D32" : totalePL < 0 ? "#C62828" : "#64748B";

  return (
    <>
      {/* ── Header con totale + pulsante nuova ─────────── */}
      <div style={stili.card}>
        <div style={stili.intestazioneCard}>
          <div style={stili.intestazioneSinistra}>
            <h3 style={stili.titoloCard}>Diario operazioni</h3>
            <div style={stili.rigaTotale}>
              <span>
                {operazioni.length} {operazioni.length === 1 ? "operazione" : "operazioni"}
              </span>
              <span>·</span>
              <span style={stili.totaleLabel}>Totale P/L netto:</span>
              <span style={{ ...stili.totaleValore, color: totaleColor }}>
                {totalePL > 0 ? "+" : ""}
                {_formatNumeroIT(totalePL, 2)} €
              </span>
            </div>
          </div>
          <button
            style={{ ...stili.btn, ...stili.btnPrimary }}
            onClick={apriNuova}
          >
            + Nuova operazione manuale
          </button>
        </div>

        {/* Filtri rapidi */}
        <div style={stili.filtriRapidiRiga}>
          {filtriRapidi.map(f => {
            const disabilitato = isChipDisabilitato(f.id);
            const titolo = tooltipChip(f.id);
            return (
              <button
                key={f.id}
                style={{
                  ...stili.chipFiltro,
                  ...(filtroRapido === f.id ? stili.chipFiltroAttivo : {}),
                  ...(disabilitato ? stili.chipFiltroDisabilitato : {}),
                }}
                onClick={() => { if (!disabilitato) cambiaFiltroRapido(f.id); }}
                disabled={disabilitato}
                title={titolo}
              >
                {f.label}
              </button>
            );
          })}
        </div>

        {/* Filtri personalizzati (solo se filtro = personalizzato) */}
        {filtroRapido === "personalizzato" && (
          <div style={stili.filtriPersRiga}>
            <div style={stili.filtroCol}>
              <label style={stili.filtroLabel}>Da data</label>
              <input
                type="date"
                style={stili.filtroInput}
                value={daData}
                onChange={(e) => setDaData(e.target.value)}
              />
            </div>
            <div style={stili.filtroCol}>
              <label style={stili.filtroLabel}>A data</label>
              <input
                type="date"
                style={stili.filtroInput}
                value={aData}
                onChange={(e) => setAData(e.target.value)}
              />
            </div>
          </div>
        )}
      </div>

      {/* ── Tabella operazioni ─────────────────────────── */}
      <div style={stili.tabellaWrap}>
        {loading ? (
          <div style={stili.loading}>Caricamento operazioni…</div>
        ) : errore ? (
          <div style={stili.vuoto}>Errore: {errore}</div>
        ) : operazioni.length === 0 ? (
          <div style={stili.vuoto}>
            Nessuna operazione presente.{" "}
            {filtroRapido === "tutto" ? "Clicca '+ Nuova operazione manuale' per iniziare." : "Prova a cambiare il filtro o clicca '+ Nuova operazione manuale'."}
          </div>
        ) : (
          <table style={stili.tabella}>
            <thead>
              <tr>
                <th style={stili.th}>Bookmaker</th>
                <th style={stili.th}>Data</th>
                <th style={stili.th}>Ora</th>
                <th style={stili.th}>Partita</th>
                <th style={stili.th}>Sistema</th>
                <th style={stili.thNum}>Size %</th>
                <th style={stili.th}>Mercato</th>
                <th style={stili.th}>Tipo</th>
                <th style={stili.thNum}>Stake</th>
                <th style={stili.thNum}>Quota effettiva</th>
                <th style={stili.th}>Esito</th>
                <th style={stili.thNum}>P/L</th>
                <th style={stili.thCenter}>Azioni</th>
              </tr>
            </thead>
            <tbody>
              {operazioni.map(op => {
                const esitoLabel = op.esito_operazione || "Open";
                const colTipo = COLORI_TIPO[op.tipo_punta_banca] || { bg: "#F1F5F9", color: "#64748B" };
                const colEsito = COLORI_ESITO[esitoLabel] || COLORI_ESITO.Open;
                const pl = op.profitto_netto_eur;
                const plColor = pl === null || pl === undefined ? "#94A3B8"
                              : pl > 0 ? "#2E7D32"
                              : pl < 0 ? "#C62828"
                              : "#64748B";
                return (
                  <tr key={op.id}>
                    {/* 1. Bookmaker */}
                    <td style={stili.td}>
                      {bookmakers[op.bookmaker_id] || `#${op.bookmaker_id}`}
                    </td>
                    {/* 2. Data */}
                    <td style={stili.td}>{celOValNa(formatData(op.data_partita))}</td>
                    {/* 3. Ora */}
                    <td style={stili.td}>{celOValNa(formatOra(op.ora_partita))}</td>
                    {/* 4. Partita */}
                    <td style={stili.td}>
                      <strong>{op.casa}</strong> – {op.trasferta}
                      {op.competizione && (
                        <div style={{ fontSize: 11, color: "#94A3B8", marginTop: 2 }}>
                          {op.competizione}
                        </div>
                      )}
                    </td>
                    {/* 5. Sistema */}
                    <td style={stili.td}>
                      {op.sistema_id ? (sistemiMappa[op.sistema_id] || op.sistema_id) : NA}
                    </td>
                    {/* 6. Size % — se 0 mostro n.a. (è stato usato Stake manuale, oppure non valorizzato) */}
                    <td style={stili.tdNum}>
                      {(op.size_pct === null || op.size_pct === undefined || Number(op.size_pct) === 0)
                        ? NA
                        : formatPct(op.size_pct)}
                    </td>
                    {/* 7. Mercato */}
                    <td style={stili.td}>{op.mercato || NA}</td>
                    {/* 8. Tipo */}
                    <td style={stili.td}>
                      <span style={{
                        ...stili.badgeTipo,
                        background: colTipo.bg, color: colTipo.color,
                      }}>
                        {op.tipo_punta_banca}
                      </span>
                    </td>
                    {/* 9. Stake (€) */}
                    <td style={stili.tdNum}>{celOValNa(formatEur(op.size_eur_snapshot))}</td>
                    {/* 10. Quota effettiva */}
                    <td style={stili.tdNum}>{celOValNa(formatNumero(op.quota_effettiva))}</td>
                    {/* 11. Esito (dropdown inline) */}
                    <td style={stili.td}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <select
                          value={esitoLabel}
                          onChange={(e) => cambiaEsitoInline(op, e.target.value)}
                          style={{
                            ...stili.esitoSelect,
                            background: colEsito.bg,
                            color: colEsito.color,
                          }}
                        >
                          <option value="Open">Open</option>
                          <option value="Vinta">Vinta</option>
                          <option value="Persa">Persa</option>
                        </select>
                        {/* v127: badge auto-chiusura pipeline */}
                        {op.chiusura_automatica && (
                          <span
                            title="Esito aggiornato automaticamente dal sistema in base al risultato finale. Se non è corretto, modifica l'operazione manualmente."
                            style={{
                              cursor: "help",
                              fontSize: 14,
                              lineHeight: 1,
                              userSelect: "none",
                            }}
                            aria-label="Chiusura automatica"
                          >
                            🤖
                          </span>
                        )}
                      </div>
                    </td>
                    {/* 12. P/L */}
                    <td style={{ ...stili.tdNum, color: plColor, fontWeight: 700 }}>
                      {celOValNa(formatEurConSegno(pl))}
                    </td>
                    {/* 13. Azioni */}
                    <td style={stili.tdCenter}>
                      <button
                        style={stili.btnIcona}
                        onClick={() => apriModifica(op)}
                        title="Modifica"
                      >
                        ✏️
                      </button>
                      <button
                        style={stili.btnIcona}
                        onClick={() => setConfermaCanc(op)}
                        title="Elimina"
                      >
                        🗑️
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Modal form ─────────────────────────── */}
      {modalAperto && (
        <FormOperazione
          operazioneEsistente={operazioneInModifica}
          onChiudi={chiudiModal}
          onSalva={dopoSalva}
        />
      )}

      {/* ── Dialog conferma cancellazione ─────────────── */}
      {confermaCanc && (
        <div style={stili.dialogOverlay} onClick={() => setConfermaCanc(null)}>
          <div style={stili.dialog} onClick={(e) => e.stopPropagation()}>
            <div style={stili.dialogTitolo}>Eliminare l'operazione?</div>
            <div style={stili.dialogTesto}>
              <strong>{confermaCanc.casa}</strong> – {confermaCanc.trasferta}
              <br />
              {formatData(confermaCanc.data_partita)} · {confermaCanc.mercato}
              <br /><br />
              L'operazione verrà eliminata definitivamente. Questa azione non può essere annullata.
            </div>
            <div style={stili.dialogAzioni}>
              <button
                style={{ ...stili.btn, ...stili.btnSecondary }}
                onClick={() => setConfermaCanc(null)}
              >
                Annulla
              </button>
              <button
                style={{ ...stili.btn, ...stili.btnDanger }}
                onClick={confermaElimina}
              >
                Elimina
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
